import { useMemo, useState } from 'react';
import { FaEye, FaEyeSlash, FaLock, FaShieldAlt, FaSpinner } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/apiService';
import './ForcePasswordGate.css';

function getPasswordScore(password) {
  return [
    password.length >= 8,
    /[A-Z]/.test(password),
    /[a-z]/.test(password),
    /\d/.test(password),
    /[^A-Za-z0-9]/.test(password),
  ].filter(Boolean).length;
}

const HINTS = ['Muito fraca', 'Fraca', 'Razoável', 'Boa', 'Forte', 'Excelente'];

export default function ForcePasswordGate({ children }) {
  const { user, loading, updateUser, logout } = useAuth();
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const score = useMemo(() => getPasswordScore(password), [password]);
  const mustChange = Boolean(user?.mustChangePassword);

  if (loading || !mustChange) {
    return children;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (score < 3) {
      setError('Crie uma senha mais forte.');
      return;
    }
    if (password !== passwordConfirm) {
      setError('As senhas não coincidem.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await authService.updatePassword({
        password,
        passwordConfirm,
      });
      if (response?.token) authService.setAccessToken(response.token);
      updateUser(response?.data?.user || null);
    } catch (err) {
      setError(err.message || 'Não foi possível atualizar a senha.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="force-pass-gate">
      <div className="force-pass-card">
        <div className="force-pass-icon" aria-hidden="true">
          <FaShieldAlt />
        </div>
        <p className="force-pass-eyebrow">Segurança da conta</p>
        <h1>Defina uma nova senha</h1>
        <p className="force-pass-lead">
          Um administrador solicitou a redefinição da sua senha. Para continuar usando o site,
          escolha uma nova senha agora.
        </p>

        <form onSubmit={handleSubmit} className="force-pass-form">
          <label htmlFor="force-password">
            Nova senha
            <div className="force-pass-field">
              <FaLock aria-hidden="true" />
              <input
                id="force-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="force-pass-toggle"
                onClick={() => setShowPassword((current) => !current)}
                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </label>

          <div className="force-pass-strength" aria-label={`Força da senha: ${score} de 5`}>
            <div>{[1, 2, 3, 4, 5].map((level) => (
              <span key={level} className={score >= level ? 'filled' : ''} />
            ))}
            </div>
            <small>{HINTS[score]}</small>
          </div>

          <label htmlFor="force-password-confirm">
            Confirmar senha
            <div className="force-pass-field">
              <FaLock aria-hidden="true" />
              <input
                id="force-password-confirm"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                required
              />
            </div>
          </label>

          {error && <p className="force-pass-error">{error}</p>}

          <button type="submit" className="force-pass-submit" disabled={submitting}>
            {submitting ? <FaSpinner className="force-pass-spin" /> : null}
            {submitting ? 'Salvando...' : 'Salvar nova senha'}
          </button>
        </form>

        <button type="button" className="force-pass-logout" onClick={() => logout()}>
          Sair da conta
        </button>
      </div>
    </div>
  );
}
